import { formatDistance, isValid, parseISO } from 'date-fns';
import { format, toZonedTime } from 'date-fns-tz';
import * as locales from 'date-fns/locale';

function getLocaleByCode(localeCode: string) {
    const localeList = Object.values(locales);
    return localeList.find((item) => item.code === localeCode) || locales.enUS;
}

export function formatDate(
    input?: string | number | Date,
    {
        short = false,
        pattern: initialPattern,
        withTime = false,
        distance = false,
        timeZone: timeZoneOption,
        localeCode = 'en-US',
        withTimeZone = true,
        useLocalTimeZone = false,
    }: {
        short?: boolean;
        pattern?: string;
        withTime?: boolean;
        distance?: boolean;
        timeZone?: string;
        localeCode?: string;
        withTimeZone?: boolean;
        useLocalTimeZone?: boolean;
    } = {},
): string {
    if (!input) {
        return '-';
    }

    let timeZone = timeZoneOption;
    if (useLocalTimeZone && !timeZone) {
        try {
            timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
        } catch {
            timeZone = undefined;
        }
    }

    let pattern = initialPattern || `MMM dd, yyyy${withTime ? ' HH:mm:ss' : ''}${withTimeZone && timeZone ? ' z' : ''}`;
    if (short) {
        pattern = pattern.replace(', yyyy', '');
    }

    let date: Date;
    let timeZoneData = '';

    if (typeof input === 'string') {
        date = parseISO(input);

        // Not an ISO date
        if (!isValid(date)) {
            const match = input.match(/[a-zA-Z]{2,}$/);
            timeZoneData = match?.[0] ?? '';
            input = input.replace(timeZoneData, '').trim();
        } else {
            const match = input.match(/Z$|[+-]\d{2}:?\d{2}$/);
            if (match && !timeZone) {
                timeZoneData = match[0];
                input = input.replace(timeZoneData, '').trim();

                if (timeZoneData === 'Z' || timeZoneData === '+0000') {
                    timeZoneData = 'UTC';
                } else {
                    timeZoneData = `UTC${timeZoneData}`;
                }
            }
        }
    }

    date = new Date(input);

    if (!isValid(date)) {
        return '-';
    }

    // Return relative time if distance flag is enabled
    if (distance) {
        return formatDistance(date, new Date(), { addSuffix: true });
    }

    // Format date (with optional timezone conversion)
    const locale = localeCode ? getLocaleByCode(localeCode) : undefined;
    let formattedDate: string;
    try {
        formattedDate = format(timeZone ? toZonedTime(date, timeZone) : date, pattern, { timeZone, locale });
    } catch {
        formattedDate = format(date, pattern, { locale });
    }

    if (timeZoneData && withTimeZone) {
        return `${formattedDate} ${timeZoneData}`;
    }

    return formattedDate;
}
